import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { entityPath, type EntityKind } from '../utils/entityRoutes';
import { normalizeRecordId, useRecordList } from './useRecordList';

/** The word for one record of a kind, for "Previous <kind>" / "Next <kind>". */
export const RECORD_NAV_LABEL: Record<EntityKind, string> = {
  vendor: 'vendor',
  quote: 'quote',
  task: 'task',
  shippingAgent: 'shipping agent',
  budgetCategory: 'budget category',
  catalogueItem: 'catalogue item',
  vehicle: 'vehicle',
  driver: 'driver',
  guideline: 'guideline',
  stationeryItem: 'stationery item',
  decorElement: 'decor element',
  calendarEvent: 'calendar event',
  event: 'event',
  menuItem: 'menu item',
  venue: 'venue',
  client: 'client',
  clientInvoice: 'invoice',
  clientQuote: 'client quote',
  payment: 'payment',
  contributor: 'contributor',
  bundle: 'bundle',
  project: 'project',
  song: 'song',
  teamMember: 'team member',
  transportAssignment: 'transport assignment',
  favourAssignment: 'favour assignment',
  inventoryItem: 'inventory item',
  fabric: 'fabric',
  fabricAssignment: 'aso-ebi order',
  asoebiDelivery: 'delivery',
  asoebiAssignment: 'assignment',
  guest: 'guest',
  procurementOrder: 'order',
  shipment: 'shipment',
  vendorBooking: 'booking',
};

export interface RecordNav {
  /** False when no list order is known for this record (deep link, or the record is not in the list). */
  available: boolean;
  /** 1-based position in the list, 0 when unavailable. */
  position: number;
  total: number;
  prevId: string | null;
  nextId: string | null;
  prevLabel: string;
  nextLabel: string;
  goPrev: () => void;
  goNext: () => void;
}

/**
 * Previous / next record of the list the user came from. The order is whatever
 * the list screen published (useRecordList), so filters and sorting match.
 * Moving away with unsaved edits goes through the same guard as any other
 * in-app navigation (useUnsavedChangesGuard covers .header-record-nav).
 */
export function useRecordNav(kind: EntityKind, currentId: string | number | null | undefined): RecordNav {
  const navigate = useNavigate();
  const list = useRecordList(kind);
  const noun = RECORD_NAV_LABEL[kind];

  return useMemo(() => {
    const current = normalizeRecordId(kind, currentId);
    const at = current === undefined ? -1 : list.indexOf(current);
    const prevId = at > 0 ? list[at - 1] : null;
    const nextId = at >= 0 && at < list.length - 1 ? list[at + 1] : null;
    const go = (id: string | null) => () => {
      const path = id ? entityPath(kind, id) : undefined;
      if (path) navigate(path);
    };
    return {
      available: at >= 0 && list.length > 1,
      position: at >= 0 ? at + 1 : 0,
      total: list.length,
      prevId,
      nextId,
      prevLabel: `Previous ${noun}`,
      nextLabel: `Next ${noun}`,
      goPrev: go(prevId),
      goNext: go(nextId),
    };
  }, [kind, currentId, list, navigate, noun]);
}
